import {api} from '../../../shared';
import {
  FundraiserModel,
  FundraisersResponse,
  CreateFundraiserDTO,
  FundraiserDetailedModel,
  FundraiserFinanceModel,
  UpdateFundraiserParams,
  FundraiserStoriesResponse,
  CreateFundraiserStoryDTO,
  UpdateFundraiserStoryDTO,
  FundraiserStoriesFilter,
  FundraiserStoryModel,
  FundraiserStoryDetailedModel,
  FundraiserDocumentsResponse,
  FundraiserDocumentsFilter,
  CreateFundraiserNotificationDTO,
} from '../model';
import {CommunicationMessagesWithMetaModel, CommunicationMessagesFilter} from '../../communication';

export const fundraisersApi = {
  getFundraisers: async (params?: {
    pageSize?: number;
    after?: string;
    query?: string;
    sortProperty?: string;
    sortOrder?: string;
    categories?: string[];
    regions?: string[];
    funds?: string[];
    statuses?: string[];
  }): Promise<FundraisersResponse> => {
    const {data} = await api.get<FundraisersResponse>('/api/fundraising/admin/appeals', {
      params,
      paramsSerializer: {
        serialize: (params) => {
          const searchParams = new URLSearchParams();

          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              // Для массивов добавляем каждое значение отдельно
              value.forEach((item) => searchParams.append(key, item));
            } else if (value !== undefined && value !== null) {
              searchParams.append(key, String(value));
            }
          });

          return searchParams.toString();
        },
      },
    });
    return data;
  },

  getFundraiserById: async (fundraiserId: string): Promise<FundraiserModel> => {
    const {data} = await api.get<FundraiserModel>(`/api/fundraising/admin/appeals/${fundraiserId}`);
    return data;
  },

  getFundraiserDetailedById: async (fundraiserId: string): Promise<FundraiserDetailedModel> => {
    const {data} = await api.get<FundraiserDetailedModel>(`/api/fundraising/admin/appeals/${fundraiserId}`);
    return data;
  },

  createFundraiser: async (dto: CreateFundraiserDTO): Promise<FundraiserModel> => {
    const {data} = await api.post<FundraiserModel>('/api/fundraising/admin/appeals', dto);
    return data;
  },

  makeUrgent: async (fundraiserId: string): Promise<void> => {
    await api.post(`/api/fundraising/admin/appeals/${fundraiserId}/urgent`);
  },

  makeNonUrgent: async (fundraiserId: string): Promise<void> => {
    await api.delete(`/api/fundraising/admin/appeals/${fundraiserId}/urgent`);
  },

  getFundraiserFinance: async (fundraiserId: string): Promise<FundraiserFinanceModel> => {
    const {data} = await api.get<FundraiserFinanceModel>(`/api/finance/admin/appeals/${fundraiserId}`);
    return data;
  },

  updateFundraiserFinance: async (
    fundraiserId: string,
    dto: {
      requiredAmount: number | null;
      commissionPercentage: number;
      end: string | null;
    },
  ): Promise<FundraiserFinanceModel> => {
    const {data} = await api.put<FundraiserFinanceModel>(`/api/finance/admin/appeals/${fundraiserId}`, dto);
    return data;
  },

  deleteFundraiser: async (fundraiserId: string): Promise<void> => {
    await api.delete(`/api/fundraising/admin/appeals/${fundraiserId}`);
  },

  // Внесение средств от пользователя
  addBenefactorTransfer: async (
    fundraiserId: string,
    transferData: {
      benefactorId: string;
      amount: number;
      serviceFee: number;
      bookkeepingNumber?: string | null;
      note?: string | null;
    },
  ): Promise<void> => {
    const {data} = await api.post<void>(
      `/api/finance/admin/appeals/${fundraiserId}/transfers/from-benefactor`,
      transferData,
    );
    return data;
  },

  // Внесение средств от проекта
  addProjectTransfer: async (
    fundraiserId: string,
    transferData: {
      amount: number;
    },
  ): Promise<void> => {
    const {data} = await api.post<void>(
      `/api/finance/admin/appeals/${fundraiserId}/transfers/from-project`,
      transferData,
    );
    return data;
  },

  updateFundraiser: async ({fundraiserId, data}: UpdateFundraiserParams): Promise<FundraiserModel> => {
    const {data: responseData} = await api.post<FundraiserModel>(
      `/api/fundraising/admin/appeals/${fundraiserId}`,
      data,
    );
    return responseData;
  },

  publishFundraiser: async (fundraiserId: string): Promise<void> => {
    await api.post(`/api/fundraising/admin/appeals/${fundraiserId}/published`);
  },

  unpublishFundraiser: async (fundraiserId: string): Promise<void> => {
    await api.delete(`/api/fundraising/admin/appeals/${fundraiserId}/published`);
  },

  // API для историй сборов
  getFundraiserStories: async (params: FundraiserStoriesFilter): Promise<FundraiserStoriesResponse> => {
    const {appealId, ...queryParams} = params;
    const {data} = await api.get<FundraiserStoriesResponse>(`/api/content/admin/appeals/${appealId}/stories`, {
      params: queryParams,
    });
    return data;
  },

  createFundraiserStory: async (appealId: string, dto: CreateFundraiserStoryDTO): Promise<FundraiserStoryModel> => {
    const {data} = await api.post<FundraiserStoryModel>(`/api/content/admin/appeals/${appealId}/stories`, dto);
    return data;
  },

  updateFundraiserStory: async (
    appealId: string,
    storyId: string,
    dto: UpdateFundraiserStoryDTO,
  ): Promise<FundraiserStoryModel> => {
    const {data} = await api.post<FundraiserStoryModel>(
      `/api/content/admin/appeals/${appealId}/stories/${storyId}`,
      dto,
    );
    return data;
  },

  deleteFundraiserStory: async (storyId: string): Promise<void> => {
    await api.delete(`/api/content/admin/stories/${storyId}`);
  },

  getFundraiserStoryDetailedById: async (appealId: string, storyId: string): Promise<FundraiserStoryDetailedModel> => {
    const {data} = await api.get<FundraiserStoryDetailedModel>(
      `/api/content/admin/appeals/${appealId}/stories/${storyId}`,
    );
    return data;
  },

  uploadFundraiserStoryPreviewImage: async (
    appealId: string,
    storyId: string,
    dto: {previewImageUploadId: string},
  ): Promise<void> => {
    await api.post(`/api/content/admin/appeals/${appealId}/stories/${storyId}/preview-image`, dto);
  },

  getFundraiserDocuments: async (
    params: FundraiserDocumentsFilter & {appealId: string},
  ): Promise<FundraiserDocumentsResponse> => {
    const {appealId, ...queryParams} = params;
    const {data} = await api.get<FundraiserDocumentsResponse>(`/api/fundraising/public/appeals/${appealId}/documents`, {
      params: queryParams,
    });
    return data;
  },

  // API для уведомлений
  getCommunicationMessages: async (
    appealId: string,
    params: CommunicationMessagesFilter = {},
  ): Promise<CommunicationMessagesWithMetaModel> => {
    const {data} = await api.get<CommunicationMessagesWithMetaModel>(
      `/api/communication/admin/appeals/${appealId}/messages`,
      {params},
    );
    return data;
  },

  // Отправка уведомления
  sendCommunicationMessage: async (appealId: string, dto: CreateFundraiserNotificationDTO): Promise<void> => {
    await api.post(`/api/communication/admin/appeals/${appealId}/messages`, dto);
  },

  // Пометить средства как собранные
  markAmountCollected: async (appealId: string): Promise<void> => {
    await api.post(`/api/fundraising/admin/appeals/${appealId}/amount-collected`);
  },

  // Завершить сбор
  completeFundraiser: async (appealId: string): Promise<void> => {
    await api.post(`/api/fundraising/admin/appeals/${appealId}/complete`);
  },

  createAppealStoryFromMaterials: async (
    appealId: string,
    body: import('../../../features/appealMaterialsStory/lib/buildCreateStoryFromMaterialsRequest').CreateStoryFromMaterialsBody,
  ): Promise<FundraiserStoryModel> => {
    const {data} = await api.post<FundraiserStoryModel>(
      `/api/content/admin/appeals/${appealId}/stories/from-materials`,
      body,
    );
    return data;
  },
};
